import { Component, OnInit, ViewChild, ViewEncapsulation, ElementRef, Output, EventEmitter, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, first, switchMap, takeUntil } from 'rxjs/operators';
import { isNil } from 'lodash';
import { MunicipalityService } from '@app/shared/services/api/municipality.service';
import { EnergyMapService } from '@app/energy-map/services/energy-map-service';
import { AuthService } from '@app/shared/services/auth.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { NullableString } from '@app/shared/models/nullable';

@Component({
  selector: 'app-settings-panel',
  templateUrl: './settings-panel.component.html',
  styleUrls: ['./settings-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SettingsPanelComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  @ViewChild('priceInput', ) priceInput: ElementRef | undefined;
  @Output() closed = new EventEmitter<boolean>();
  public error = false;
  public inputPriceChange: BehaviorSubject<NullableString> = new BehaviorSubject<NullableString>(null);
  public isUpdating = false;
  public price: BehaviorSubject<number> = new BehaviorSubject<number>(15);
  private computation: PanelSettings = {} as PanelSettings;
  private maxPrice = 100;
  private minPrice = 10;
  private priceInEuros = this.price.value / 100;

  constructor(
    public authService: AuthService,
    private energyMapService: EnergyMapService,
    private municipalityService: MunicipalityService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {}

  public ngOnInit(): void {
    this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged(),
        switchMap((municipality: MapStoreMunicipality) => this.municipalityService.getPanelSettings(municipality.key)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((data: Array<{ grossElectricityPricePerUnit: number }>) => {
        this.computation = {
          ...data[0],
          electricityCostMultiplied: data[0].grossElectricityPricePerUnit * 100,
        };
        this.price.next(this.computation.electricityCostMultiplied);
      });

    this.price.pipe(takeUntil(this.unsubscribe$)).subscribe((value: number) => {
      this.error = false;
      this.priceInEuros = value / 100;
    });

    this.inputPriceChange
      .pipe(
        debounceTime(500),
        filter((value: NullableString): value is string => !isNil(value)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((value: string) => this.priceChange(value));
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public updatePrice(): void {
    if (this.error) {
      return;
    }
    const currentElectricityCost = this.computation.grossElectricityPricePerUnit;
    if (this.priceInEuros && currentElectricityCost !== this.priceInEuros) {
      this.isUpdating = true;
      this.computation.grossElectricityPricePerUnit = this.priceInEuros;
      this.computation.electricityCostMultiplied = this.priceInEuros * 100;
      this.municipalityService
        .updateMunicipality({
          guid: this.userMunicipalitiesService.currentMunicipality.guid,
          municipalityKey: this.userMunicipalitiesService.currentMunicipality.key,
          grossElectricityPricePerUnit: this.priceInEuros,
        })
        .pipe(
          finalize(() => (this.isUpdating = false)),
          first()
        )
        .subscribe(() => this.energyMapService.grossElectricityCostChanged(this.priceInEuros));
    }
  }

  private priceChange(value: string): void {
    const valid = this.priceValidator(value);

    if (valid) {
      this.error = false;
      this.price.next(Number(value));
    } else {
      this.error = true;
    }
  }

  private priceValidator(value: string): boolean {
    if (!this.isFormatValid(value)) {
      return false;
    }

    const price = Number(value);
    return this.isValueInRange(price);
  }

  private isFormatValid(value: string): boolean {
    const pattern = /^(\d{2,3}(,\d{2,3})?|\d*,\d{2,3})$/;
    return pattern.test(value);
  }

  private isValueInRange(value: number): boolean {
    return value >= this.minPrice && value <= this.maxPrice;
  }
}

interface PanelSettings {
  grossElectricityPricePerUnit: number;
  electricityCostMultiplied: number;
}
